/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * UI-edge validation: validateAndFilterSavedViews, prepareSavedViewsForUI, validateSelectedViewId.
 */

import { logger } from "@/lib/utils/logger";

import type { NormalizedSavedView } from "./types";
import { isValidNormalizedSavedView } from "./validation";

function devConsoleWarn(
  message: string,
  context?: Record<string, unknown>,
): void {
  if (process.env.NODE_ENV === "development") {
    logger.warn(message, context);
  }
}

export type InputViewForValidation<TFilters = Record<string, unknown>> =
  | { id: string; name: string; filters: TFilters }
  | { view_id: string; name: string; filters: TFilters }
  | NormalizedSavedView<TFilters>;

export function validateAndFilterSavedViews<TFilters = Record<string, unknown>>(
  views: InputViewForValidation<TFilters>[],
  context?: string,
): NormalizedSavedView<TFilters>[] {
  if (!Array.isArray(views)) {
    const warningMessage = `validateAndFilterSavedViews: views is not an array${context ? ` (${context})` : ""}`;
    devConsoleWarn(warningMessage, { views });
    return [];
  }

  const validated: NormalizedSavedView<TFilters>[] = [];
  let filteredCount = 0;

  for (let i = 0; i < views.length; i++) {
    const view = views[i];
    if (!view || typeof view !== "object") {
      filteredCount++;
      continue;
    }

    if ("id" in view && !("view_id" in view)) {
      if (isValidNormalizedSavedView(view)) {
        validated.push(view);
      } else {
        filteredCount++;
        const warningMessage = `validateAndFilterSavedViews: Skipping invalid normalized view${context ? ` (${context})` : ""} (index ${i})`;
        devConsoleWarn(warningMessage, {
          index: i,
          viewId: (view as Partial<NormalizedSavedView<TFilters>>).id,
          viewName: (view as Partial<NormalizedSavedView<TFilters>>).name,
          hasFilters:
            (view as Partial<NormalizedSavedView<TFilters>>).filters !==
            undefined,
        });
      }
      continue;
    }

    if ("view_id" in view) {
      const viewWithViewId = view as {
        view_id: string;
        name: string;
        filters: TFilters;
        [key: string]: unknown;
      };
      const viewId = viewWithViewId.view_id;
      if (
        viewId &&
        typeof viewId === "string" &&
        viewId.trim().length > 0 &&
        viewWithViewId.name &&
        typeof viewWithViewId.name === "string" &&
        viewWithViewId.name.trim().length > 0 &&
        viewWithViewId.filters !== undefined &&
        viewWithViewId.filters !== null
      ) {
        const normalized: NormalizedSavedView<TFilters> = {
          id: viewId.trim(),
          name: viewWithViewId.name.trim(),
          filters: viewWithViewId.filters,
        };

        if (typeof viewWithViewId.view_type === "string") {
          normalized.view_type = viewWithViewId.view_type;
        }
        if (typeof viewWithViewId.is_org_preset === "boolean") {
          normalized.is_org_preset = viewWithViewId.is_org_preset;
        }
        if (typeof viewWithViewId.is_default === "boolean") {
          normalized.is_default = viewWithViewId.is_default;
        }
        if (
          viewWithViewId.sort_by === null ||
          typeof viewWithViewId.sort_by === "string"
        ) {
          normalized.sort_by = viewWithViewId.sort_by ?? null;
        }
        if (
          viewWithViewId.sort_order === null ||
          viewWithViewId.sort_order === "asc" ||
          viewWithViewId.sort_order === "desc"
        ) {
          normalized.sort_order = viewWithViewId.sort_order ?? null;
        }
        if (
          viewWithViewId.columns === null ||
          Array.isArray(viewWithViewId.columns)
        ) {
          normalized.columns = viewWithViewId.columns ?? null;
        }
        if (typeof viewWithViewId.created_at === "string") {
          normalized.created_at = viewWithViewId.created_at;
        }
        if (typeof viewWithViewId.updated_at === "string") {
          normalized.updated_at = viewWithViewId.updated_at;
        }

        validated.push(normalized);
      } else {
        filteredCount++;
        const warningMessage = `validateAndFilterSavedViews: Skipping view with invalid view_id${context ? ` (${context})` : ""} (index ${i})`;
        devConsoleWarn(warningMessage, {
          index: i,
          viewId,
          viewName: viewWithViewId.name,
          idType: typeof viewId,
          idLength: typeof viewId === "string" ? viewId.length : 0,
        });
      }
      continue;
    }

    if ("id" in view) {
      const viewWithId = view as {
        id: string;
        name: string;
        filters: TFilters;
      };
      const viewId = viewWithId.id;
      if (
        viewId &&
        typeof viewId === "string" &&
        viewId.trim().length > 0 &&
        viewWithId.name &&
        typeof viewWithId.name === "string" &&
        viewWithId.name.trim().length > 0 &&
        viewWithId.filters !== undefined &&
        viewWithId.filters !== null
      ) {
        validated.push({
          id: viewId.trim(),
          name: viewWithId.name.trim(),
          filters: viewWithId.filters,
        });
      } else {
        filteredCount++;
        const warningMessage = `validateAndFilterSavedViews: Skipping view with invalid id${context ? ` (${context})` : ""} (index ${i})`;
        devConsoleWarn(warningMessage, {
          index: i,
          viewId,
          viewName: viewWithId.name,
          idType: typeof viewId,
          idLength: typeof viewId === "string" ? viewId.length : 0,
        });
      }
      continue;
    }

    filteredCount++;
    const warningMessage = `validateAndFilterSavedViews: Skipping view with no valid id/view_id${context ? ` (${context})` : ""} (index ${i})`;
    devConsoleWarn(warningMessage, {
      index: i,
      view,
    });
  }

  if (filteredCount > 0) {
    const warningMessage = `validateAndFilterSavedViews: Filtered out ${filteredCount} invalid view${filteredCount === 1 ? "" : "s"}${context ? ` (${context})` : ""}`;
    devConsoleWarn(warningMessage, {
      total: views.length,
      valid: validated.length,
      filtered: filteredCount,
    });
  }

  return validated;
}

export function prepareSavedViewsForUI<TFilters = Record<string, unknown>>(
  views: InputViewForValidation<TFilters>[],
  options?: {
    context?: string;
    excludeIds?: string[];
  },
): NormalizedSavedView<TFilters>[] {
  const { context, excludeIds = [] } = options ?? {};

  const validated = validateAndFilterSavedViews<TFilters>(views, context);

  if (excludeIds.length > 0) {
    return validated.filter((view) => !excludeIds.includes(view.id));
  }

  return validated;
}

export function validateSelectedViewId<TFilters = Record<string, unknown>>(
  selectedViewId: string | undefined,
  views: NormalizedSavedView<TFilters>[],
): string | undefined {
  if (!selectedViewId) {
    return undefined;
  }

  if (
    typeof selectedViewId !== "string" ||
    selectedViewId.trim().length === 0
  ) {
    return undefined;
  }

  const trimmedId = selectedViewId.trim();

  const isValid = views.some((view) => view.id === trimmedId);

  return isValid ? trimmedId : undefined;
}
